import { useEffect, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import CustomButton from "../../../../components/CustomButton";
import ModalWrapper from "../../../../components/Modal/ModalWrapper";
import TextInput from "../../../../components/misc/TextInput";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../../helpers/apiHelper";
import { useDebounce } from "../../../../hooks/useDebounce";
import { postGetProducts } from "../../../../redux/api/inventory.api";
import { postAddReceivedLabeledProduct } from "../../../../redux/api/receipt.api";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { InventoryProductsListInterface } from "../../../../redux/interfaces/inventory.interface";
import { LabeledProductInterface } from "../../../../redux/interfaces/receipt.interface";
import Text from "../../../../styleguide/Text";

interface AddReceivedProductModalProps {
  show: boolean;
  handleClose: () => void;
  receptionId?: string | number;
  labeledProduct: LabeledProductInterface;
  onProductAdded: () => void | Promise<void>;
}

interface FormValues {
  productName: string;
  productCode: string;
  sn: string;
  selectedProduct: InventoryProductsListInterface | null;
  lot: string;
  receivedQuantity: number | string;
  sendToStorage: number | string;
  sendToWarehouse: number | string;
}

const initialValues: FormValues = {
  productName: "",
  productCode: "",
  sn: "",
  selectedProduct: null,
  lot: "",
  receivedQuantity: "",
  sendToStorage: "",
  sendToWarehouse: "",
};

const quantitySchema = Yup.number()
  .typeError("Cantitatea trebuie sa fie un numar")
  .integer("Cantitatea trebuie sa fie un numar intreg")
  .min(0, "Cantitatea nu poate fi negativa")
  .required("Cantitatea trebuie specificata");

const productSchema = Yup.object({
  selectedProduct: Yup.object()
    .nullable()
    .required("Produsul trebuie selectat"),
  lot: Yup.string().trim().required("Lotul trebuie specificat"),
  receivedQuantity: Yup.number()
    .typeError("Cantitatea trebuie sa fie un numar")
    .integer("Cantitatea trebuie sa fie un numar intreg")
    .positive("Cantitatea trebuie sa fie mai mare decat 0")
    .required("Cantitatea trebuie specificata"),
  sendToStorage: quantitySchema,
  sendToWarehouse: quantitySchema,
}).test(
  "allocated-quantity",
  "Suma cantitatilor trimise trebuie sa fie egala cu cantitatea primita",
  function (values) {
    const receivedQuantity = Number(values?.receivedQuantity);
    const allocatedQuantity =
      Number(values?.sendToStorage) + Number(values?.sendToWarehouse);

    if (
      !Number.isFinite(receivedQuantity) ||
      !Number.isFinite(allocatedQuantity) ||
      allocatedQuantity === receivedQuantity
    ) {
      return true;
    }

    return this.createError({
      path: "sendToWarehouse",
      message:
        "Trimite Storage + Trimite Depozit trebuie sa fie egal cu Cantitate primita",
    });
  },
);

function ProductSearch({
  show,
  productName,
  productCode,
  sn,
}: {
  show: boolean;
  productName: string;
  productCode: string;
  sn: string;
}) {
  const dispatch = useAppDispatch();
  const debouncedProductName = useDebounce(productName, 500);
  const debouncedProductCode = useDebounce(productCode, 500);
  const debouncedSn = useDebounce(sn, 500);

  useEffect(() => {
    const nameFilter = debouncedProductName.toString().trim();
    const codeFilter = debouncedProductCode.toString().trim();
    const snFilter = debouncedSn.toString().trim();
    const hasSearchValue = [nameFilter, codeFilter, snFilter].some(
      (value) => value.length >= 2,
    );

    if (!show || !hasSearchValue) return;

    dispatch(
      postGetProducts({
        page: 1,
        per_page: 20,
        numeProdusErp: nameFilter,
        codProdusErp: codeFilter,
        sn: snFilter,
      }),
    );
  }, [debouncedProductCode, debouncedProductName, debouncedSn, dispatch, show]);

  return null;
}

function AddReceivedProductModal({
  show,
  handleClose,
  receptionId,
  labeledProduct,
  onProductAdded,
}: AddReceivedProductModalProps) {
  const dispatch = useAppDispatch();
  const [formikValues, setFormikValues] = useState<FormValues>(initialValues);
  const [isLoading, setIsLoading] = useState(false);

  const {
    products: {
      data: { produseWms },
      isLoadingProducts,
    },
  } = useAppSelector((state) => state.inventory);

  const closeModal = () => {
    setFormikValues({ ...initialValues });
    handleClose();
  };

  const handleSaveReceivedProduct = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>,
  ) => {
    const labeledProductId =
      labeledProduct.idMarfaTrimisaEtichetat ?? labeledProduct.id;

    if (
      !receptionId ||
      labeledProductId === undefined ||
      !values.selectedProduct
    ) {
      setSubmitting(false);
      return;
    }

    setIsLoading(true);

    const resultAction = await dispatch(
      postAddReceivedLabeledProduct({
        ...labeledProduct,
        selectedProduct: values.selectedProduct,
        idFluxReceptie: receptionId,
        lot: values.lot.trim(),
        cantitatePrimita: Number(values.receivedQuantity),
        trimiteStorage: Number(values.sendToStorage),
        trimiteDepozit: Number(values.sendToWarehouse),
      }),
    );

    if (
      checkEndpointStatus(resultAction, postAddReceivedLabeledProduct) !==
      endpointStatus.pending
    ) {
      setIsLoading(false);
      setSubmitting(false);
    }

    if (
      checkEndpointStatus(resultAction, postAddReceivedLabeledProduct) ===
      endpointStatus.fulfilled
    ) {
      closeModal();
      await onProductAdded();
    }
  };

  return (
    <ModalWrapper
      title="Adauga marfa primita"
      show={show}
      handleClose={closeModal}>
      <Formik
        enableReinitialize
        initialValues={formikValues}
        validationSchema={productSchema}
        validateOnMount
        onSubmit={handleSaveReceivedProduct}>
        {({
          values,
          isValid,
          errors,
          touched,
          getFieldProps,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
        }) => {
          const hasSearchValue = [
            values.productName,
            values.productCode,
            values.sn,
          ].some((value) => value.trim().length >= 2);

          const clearSelectedProduct = () => {
            if (values.selectedProduct) {
              setFieldValue("selectedProduct", null, false);
            }
          };

          return (
            <form onSubmit={handleSubmit} noValidate>
              <Text className="mb-4" variant="h1">
                Cauta produsul
              </Text>
              <ProductSearch
                show={show}
                productName={values.productName}
                productCode={values.productCode}
                sn={values.sn}
              />

              <TextInput
                name="sn"
                label="SN"
                placeholder="SN"
                withClearButton
                inputProps={{
                  ...getFieldProps("sn"),
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    clearSelectedProduct();
                    setFieldValue("sn", event.target.value);
                  },
                }}
              />

              <TextInput
                name="productCode"
                label="Cod produs"
                placeholder="Cod produs"
                withClearButton
                inputProps={{
                  ...getFieldProps("productCode"),
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                    clearSelectedProduct();
                    setFieldValue("productCode", event.target.value);
                  },
                }}
              />

              <Autocomplete
                options={produseWms}
                value={values.selectedProduct}
                loading={isLoadingProducts}
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    clearSelectedProduct();
                    setFieldValue("productName", value);
                  }
                }}
                onChange={(_, value) => {
                  setFieldValue("selectedProduct", value, true);
                  if (value) {
                    setFieldValue("productName", value.numeProdusErp, false);
                  }
                }}
                onBlur={() => setFieldTouched("selectedProduct", true)}
                filterOptions={(options) => options}
                getOptionLabel={(option) =>
                  `${option.numeProdusErp} (${option.codProdusErp})`
                }
                isOptionEqualToValue={(option, value) =>
                  option.idProdus === value.idProdus
                }
                noOptionsText={
                  hasSearchValue
                    ? "Nu au fost gasite produse"
                    : "Introduceti minimum 2 caractere intr-un filtru"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cauta produs"
                    placeholder="Nume produs"
                    error={
                      touched.selectedProduct && Boolean(errors.selectedProduct)
                    }
                    helperText={
                      touched.selectedProduct
                        ? errors.selectedProduct?.toString()
                        : undefined
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingProducts && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Text className="mb-4 mt-8" variant="h1">
                Adauga date produs
              </Text>
              <TextInput
                className="mt-6"
                name="lot"
                label="Lot"
                placeholder="Lot"
                error={errors["lot"]}
                touched={touched["lot"]}
                inputProps={getFieldProps("lot")}
              />

              <TextInput
                name="receivedQuantity"
                label="Cantitate primita"
                type="number"
                error={errors["receivedQuantity"]}
                touched={touched["receivedQuantity"]}
                inputProps={{
                  ...getFieldProps("receivedQuantity"),
                  inputProps: { min: 1, step: 1 },
                }}
                onKeyDown={(event) => {
                  if (["e", "E", "+", "-", ".", ","].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                onlyPositiveNumbers
              />

              <TextInput
                name="sendToStorage"
                label="Trimite Storage"
                type="number"
                error={errors["sendToStorage"]}
                touched={touched["sendToStorage"]}
                inputProps={{
                  ...getFieldProps("sendToStorage"),
                  inputProps: { min: 0, step: 1 },
                }}
                onKeyDown={(event) => {
                  if (["e", "E", "+", "-", ".", ","].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                onlyPositiveNumbers
              />

              <TextInput
                name="sendToWarehouse"
                label="Trimite Depozit"
                type="number"
                error={errors["sendToWarehouse"]}
                touched={touched["sendToWarehouse"]}
                inputProps={{
                  ...getFieldProps("sendToWarehouse"),
                  inputProps: { min: 0, step: 1 },
                }}
                onKeyDown={(event) => {
                  if (["e", "E", "+", "-", ".", ","].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                onlyPositiveNumbers
              />

              <div className="flex justify-end">
                <CustomButton
                  type="submit"
                  variant="contained"
                  icon="ADD"
                  loading={isSubmitting || isLoading}
                  disabled={
                    !values.selectedProduct || !isValid || isSubmitting
                  }>
                  Adauga
                </CustomButton>
              </div>
            </form>
          );
        }}
      </Formik>
    </ModalWrapper>
  );
}

export default AddReceivedProductModal;
